"use client";

import Fuse from "fuse.js";
import { useMemo } from "react";

import { NoArtistInstrumentModelMixedModel } from "@/components/selectArtistModal/NoArtistInstrumentModelMixedModel";
import { VoiceGrid } from "@/components/selectArtistModal/VoiceGrid";
import { nameFuseConfig } from "@/lib/fuse/nameFuseConfig";
import { useNamedSelectArtistFilters } from "@/lib/zustand/useSelectArtistFilters";
import {
  ArtistInstumentSaved,
  MixedModelConstructedNoDates,
  UserNoDates,
} from "@/types/types";
import { createMixedModelName } from "@/utils/createMixedModelName";

export function MixedTab({
  confirmedDoneMixedModels,
  handleClickArtist,
  closeModal,
  user,
  savedArtistsInstrumentsIds,
  namespace,
}: {
  confirmedDoneMixedModels: MixedModelConstructedNoDates[] | null;
  handleClickArtist: (artistId: string) => void;
  closeModal: () => void;
  user: UserNoDates | null;
  savedArtistsInstrumentsIds: ArtistInstumentSaved["artistInstrumentId"][];
  namespace: string;
}) {
  const useSelectArtistFilters = useNamedSelectArtistFilters((state) =>
    state.getNamedStore(namespace)
  );
  const searchString = useSelectArtistFilters((state) => state.searchString);

  const noMixedModels =
    confirmedDoneMixedModels === null || confirmedDoneMixedModels.length === 0;

  if (noMixedModels) {
    return (
      <NoArtistInstrumentModelMixedModel text="No mixed voices created." />
    );
  }

  const modelsWithName = useMemo(
    () =>
      confirmedDoneMixedModels !== null ?
        confirmedDoneMixedModels.map((model) => ({
          ...model,
          name: createMixedModelName(model),
        }))
      : [],
    [confirmedDoneMixedModels]
  );

  const fuse = useMemo(
    () => new Fuse(modelsWithName, nameFuseConfig),
    [modelsWithName]
  );

  // Filter mixed models based on search
  const filteredMixedModels =
    searchString !== "" ?
      fuse.search(searchString).map((result) => result.item)
    : modelsWithName;

  const noFilteredMixedModels = filteredMixedModels.length === 0;

  return noFilteredMixedModels ?
      <NoArtistInstrumentModelMixedModel text="No mixed voices found. Try changing your search term." />
    : <VoiceGrid
        voices={filteredMixedModels}
        handleClickArtist={(artistId) => {
          handleClickArtist(artistId);
          closeModal();
        }}
        user={user}
        savedArtistsInstrumentsIds={savedArtistsInstrumentsIds}
      />;
}
